import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowsClockwise, User, ClockCounterClockwise, Path } from "@phosphor-icons/react";

const DEFAULT_CENTER = [43.6108, 3.9662]; // Saint-Aunès area

// Extracted map style constants — prevents new object refs on every render
// which would break Leaflet's polyline / marker reconciliation
const START_MARKER_STYLE = { color: "#10B981", fillColor: "#10B981", fillOpacity: 1, weight: 3 };
const END_MARKER_STYLE = { color: "#EF4444", fillColor: "#EF4444", fillOpacity: 1, weight: 3 };

function ymdToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

// Total distance (m) of a polyline
function totalDistance(pings) {
  if (!pings || pings.length < 2) return 0;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  let s = 0;
  for (let i = 1; i < pings.length; i++) {
    const a = pings[i - 1];
    const b = pings[i];
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
    s += 2 * R * Math.asin(Math.sqrt(h));
  }
  return s;
}

export default function AdminTraces() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canView = user && (user.role === "admin" || user.role === "compta" || user.role === "associate");

  const [date, setDate] = useState(ymdToday());
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  // "Tous" overlay: shows every tracked user's polyline on the same map,
  // colored by user, so admin/compta can compare routes at a glance.
  const [showAll, setShowAll] = useState(false);
  const [allTraces, setAllTraces] = useState([]);

  useEffect(() => {
    if (!canView) return;
    loadUsers();
    setSelectedUserId(null);
    setTrace(null);
    setAllTraces([]);
  }, [date]);

  useEffect(() => {
    if (!selectedUserId || showAll) return;
    loadTrace(selectedUserId);
  }, [selectedUserId, showAll]);

  // "Tous" mode — fetch every tracked user's trace in parallel so we can overlay them.
  useEffect(() => {
    if (!showAll || users.length === 0) return;
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          users.map((u) =>
            api.get("/admin/traces", { params: { user_id: u.user_id, date } })
              .then((r) => ({ ...r.data, color: u.color, name: u.name }))
              .catch(() => null),
          ),
        );
        setAllTraces(results.filter((r) => r && (r.pings || []).length >= 2));
      } finally { setLoading(false); }
    })();
  }, [showAll, users, date]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/traces/users", { params: { date } });
      setUsers(r.data);
      // Auto-select the first tech WITH pings (skip empty ones).
      const firstWithData = (r.data || []).find((u) => u.count > 0);
      if (firstWithData && !selectedUserId) setSelectedUserId(firstWithData.user_id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadTrace = async (userId) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/traces", { params: { user_id: userId, date } });
      setTrace(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const pings = trace?.pings || [];
  const polyline = useMemo(() => pings.map((p) => [p.latitude, p.longitude]), [pings]);
  const startPos = useMemo(() => (pings.length > 0 ? [pings[0].latitude, pings[0].longitude] : null), [pings]);
  const endPos = useMemo(
    () => (pings.length > 0 ? [pings[pings.length - 1].latitude, pings[pings.length - 1].longitude] : null),
    [pings],
  );
  const distanceKm = useMemo(() => (totalDistance(pings) / 1000).toFixed(2), [pings]);
  const activeMinutes = useMemo(() => {
    if (pings.length < 2) return 0;
    const first = new Date(pings[0].timestamp).getTime();
    const last = new Date(pings[pings.length - 1].timestamp).getTime();
    return Math.round((last - first) / 60000);
  }, [pings]);
  const traceColor = trace?.user?.color || "#005CFF";
  const polylineStyle = useMemo(() => ({ color: traceColor, weight: 4, opacity: 0.85 }), [traceColor]);
  const center = polyline.length > 0 ? polyline[Math.floor(polyline.length / 2)] : DEFAULT_CENTER;

  if (!canView) {
    return (
      <div className="p-6 text-center text-white/60">
        Accès réservé aux gérants et associés.
        <button onClick={() => nav("/")} className="mt-3 block mx-auto text-[#7CB2FF] underline">Retour</button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="admin-traces-page">
      {/* Header controls */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <MapPin size={20} className="text-emerald-400" weight="duotone" />
          <div className="font-display font-semibold">Tracés journaliers</div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <label className="text-xs text-white/60 uppercase tracking-widest">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={ymdToday()}
            className="bg-[#111] border border-white/10 rounded-lg h-9 px-3 text-sm"
            data-testid="traces-date-input"
          />
          <button
            onClick={loadUsers}
            disabled={loading}
            className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 flex items-center gap-1.5 text-xs"
            data-testid="traces-refresh-btn"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Rafraîchir
          </button>
          <button
            onClick={() => setShowAll((v) => !v)}
            disabled={users.length === 0}
            className={`h-9 px-3 rounded-lg border flex items-center gap-1.5 text-xs transition-colors ${showAll ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 hover:bg-white/10 text-white/70"} disabled:opacity-40`}
            data-testid="traces-toggle-all"
            title="Superposer tous les tracés du jour"
          >
            <User size={14} /> Tous ({users.filter((u) => u.count > 0).length})
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* User list */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-2 max-h-[70vh] overflow-y-auto">
          {users.length === 0 ? (
            <div className="p-6 text-center text-xs text-white/50">
              Aucun technicien actif configuré.
            </div>
          ) : (
            <>
              {users.every((u) => u.count === 0) && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-[11px] text-amber-100 mb-2" data-testid="traces-no-pings-hint">
                  <strong>Aucun ping GPS pour cette date.</strong>
                  <div className="text-amber-100/80 mt-1">
                    Vérifie que l&apos;app BKPRO Pro Tech est ouverte au moins une fois dans la journée sur le téléphone de chaque tech, et que la permission GPS est activée.
                  </div>
                </div>
              )}
              {users.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => u.count > 0 && setSelectedUserId(u.user_id)}
                  disabled={u.count === 0}
                  className={`w-full text-left rounded-xl p-3 flex items-center gap-3 mb-1 transition-colors ${
                    selectedUserId === u.user_id ? "bg-[#005CFF]/15 border border-[#005CFF]/30"
                      : u.count === 0 ? "bg-white/[0.02] border border-transparent opacity-50 cursor-not-allowed"
                      : "bg-white/[0.02] border border-transparent hover:bg-white/5"
                  }`}
                  data-testid={`traces-user-${u.user_id}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: u.color || "#005CFF", boxShadow: u.count > 0 ? `0 0 8px ${u.color || "#005CFF"}` : "none" }}
                  />
                  <User size={14} className="text-white/40" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[10px] text-white/50 capitalize">
                      {u.role}
                      {u.count === 0 && <span className="ml-1 text-amber-300/80">· aucun ping</span>}
                    </div>
                  </div>
                  <div className={`text-[10px] tabular-nums ${u.count > 0 ? "text-white/60" : "text-white/30"}`}>
                    {u.count > 0 ? `${u.count} pts` : "—"}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>

        {/* Map + stats */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden flex flex-col min-h-[500px]">
          {/* Stats bar */}
          {trace && trace.user && (
            <div className="p-3 border-b border-white/5 flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <User size={14} className="text-white/40" />
                <span className="font-semibold">{trace.user.name}</span>
              </div>
              <span className="text-white/30">·</span>
              <div className="flex items-center gap-1.5">
                <Path size={14} className="text-emerald-400" />
                <span className="tabular-nums">{distanceKm} km</span>
              </div>
              <span className="text-white/30">·</span>
              <div className="flex items-center gap-1.5">
                <ClockCounterClockwise size={14} className="text-[#7CB2FF]" />
                <span className="tabular-nums">{activeMinutes} min actif</span>
              </div>
              <span className="text-white/30">·</span>
              <div className="text-white/60 tabular-nums">{pings.length} points</div>
              {pings.length > 0 && (
                <>
                  <span className="text-white/30">·</span>
                  <div className="text-white/60">
                    {fmtTime(pings[0].timestamp)} → {fmtTime(pings[pings.length - 1].timestamp)}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex-1 relative bg-[#0A0A0A] min-h-[400px]">
            <MapContainer center={center} zoom={12} className="w-full h-full" data-testid="traces-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* Multi-user overlay mode */}
              {showAll && allTraces.map((t) => {
                const line = t.pings.map((p) => [p.latitude, p.longitude]);
                const startP = [t.pings[0].latitude, t.pings[0].longitude];
                const endP = [t.pings[t.pings.length - 1].latitude, t.pings[t.pings.length - 1].longitude];
                const key = t.user?.id || t.name;
                return [
                  <Polyline key={`l-${key}`} positions={line} pathOptions={{ color: t.color, weight: 4, opacity: 0.75 }} />,
                  <CircleMarker key={`s-${key}`} center={startP} radius={6} pathOptions={{ color: t.color, fillColor: t.color, fillOpacity: 0.85, weight: 2 }}>
                    <Popup><b>{t.name}</b><br/>Départ · {fmtTime(t.pings[0].timestamp)}</Popup>
                  </CircleMarker>,
                  <CircleMarker key={`e-${key}`} center={endP} radius={7} pathOptions={{ color: t.color, fillColor: "#fff", fillOpacity: 1, weight: 2 }}>
                    <Popup><b>{t.name}</b><br/>Dernière position · {fmtTime(t.pings[t.pings.length - 1].timestamp)}</Popup>
                  </CircleMarker>,
                ];
              })}
              {/* Single-user mode (legacy) */}
              {!showAll && polyline.length >= 2 && (
                <Polyline positions={polyline} pathOptions={polylineStyle} />
              )}
              {!showAll && pings.length > 0 && (
                <>
                  <CircleMarker center={startPos} radius={8} pathOptions={START_MARKER_STYLE}>
                    <Popup><b>Départ</b><br />{fmtTime(pings[0].timestamp)}</Popup>
                  </CircleMarker>
                  <CircleMarker center={endPos} radius={8} pathOptions={END_MARKER_STYLE}>
                    <Popup><b>Dernière position</b><br />{fmtTime(pings[pings.length - 1].timestamp)}</Popup>
                  </CircleMarker>
                </>
              )}
            </MapContainer>
            {!trace && !showAll && (
              <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm bg-black/40 backdrop-blur-sm">
                {users.length === 0 ? "Aucune donnée pour cette date" : "Sélectionne un technicien pour voir son tracé"}
              </div>
            )}
            {showAll && allTraces.length > 0 && (
              <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-lg p-2 text-[10px] max-w-[180px] max-h-[250px] overflow-y-auto space-y-1" data-testid="traces-legend">
                <div className="text-white/50 uppercase tracking-widest mb-1">Légende ({allTraces.length})</div>
                {allTraces.map((t) => (
                  <div key={t.name} className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: t.color }} />
                    <span className="text-white/90 truncate">{t.name}</span>
                    <span className="text-white/40 tabular-nums ml-auto">{t.pings.length}pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
